/**
 * Non-money formatting: counts, percentages, dates, durations.
 * Money formatting lives in money.ts and is rendered through MoneyValue.
 * All output uses Western digits (numberingSystem: latn), the UAE banking norm.
 */
import { localeDefinition, type LocaleDefinition } from '../i18n/locales';

import { BUSINESS_TZ, parseDateOnly, parseInstant } from './dates';

const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>();
function memo<T extends Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>(
  key: string,
  make: () => T,
): T {
  let f = cache.get(key) as T | undefined;
  if (!f) {
    f = make();
    cache.set(key, f);
  }
  return f;
}

let current: LocaleDefinition = localeDefinition('en');
let currentLocale = current.intl;
/** Called by the i18n bootstrap when the UI language changes. Calendar is always Gregorian, digits Western. */
export function setFormatLocale(locale: string) {
  current = localeDefinition(locale);
  currentLocale = current.intl;
  cache.clear();
}

export function formatInteger(value: number): string {
  return memo(`int|${currentLocale}`, () => new Intl.NumberFormat(currentLocale, { numberingSystem: 'latn', maximumFractionDigits: 0 })).format(value);
}

/** "2.00" -> "2.00%" */
export function formatPercent(value: string, decimals = 2): string {
  return `${memo(`pct|${decimals}`, () => new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })).format(value as Intl.StringNumericLiteral)}%`;
}

function dateFormatter(kind: 'date' | 'datetime' | 'time' | 'month' | 'monthShort' | 'weekday') {
  return memo(`dt|${kind}|${currentLocale}`, () => {
    const base: Intl.DateTimeFormatOptions = { numberingSystem: 'latn', calendar: 'gregory' };
    switch (kind) {
      case 'date':
        return new Intl.DateTimeFormat(currentLocale, { ...base, day: '2-digit', month: current.monthStyle, year: 'numeric', timeZone: 'UTC' });
      case 'datetime':
        return new Intl.DateTimeFormat(currentLocale, { ...base, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: BUSINESS_TZ });
      case 'time':
        return new Intl.DateTimeFormat(currentLocale, { ...base, hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: BUSINESS_TZ });
      case 'month':
        return new Intl.DateTimeFormat(currentLocale, { ...base, month: 'long', year: 'numeric', timeZone: 'UTC' });
      case 'monthShort':
        return new Intl.DateTimeFormat(currentLocale, { ...base, month: 'short', year: '2-digit', timeZone: 'UTC' });
      case 'weekday':
        return new Intl.DateTimeFormat(currentLocale, { ...base, weekday: 'short', day: '2-digit', month: 'short', timeZone: BUSINESS_TZ });
    }
  });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** English keeps fixed three-letter months ("Sep", never "Sept"); other languages use Intl. */
function dayMonthYear(date: Date): string {
  if (current.intl !== 'en-GB') return dateFormatter('date').format(date);
  return `${String(date.getUTCDate()).padStart(2, '0')} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/** Date-only "2026-10-07" or instant -> "07 Oct 2026" (unambiguous, banking convention). */
export function formatDate(value: string): string {
  // Imported records can lack a date (e.g. a tracker has no date of birth).
  if (!value) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return dayMonthYear(parseDateOnly(value));
  const instant = parseInstant(value);
  const dubaiDay = new Intl.DateTimeFormat('en-CA', { timeZone: BUSINESS_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(instant);
  return dayMonthYear(parseDateOnly(dubaiDay));
}

/** Instant -> "07 Oct 2026, 14:10" / "07 أكتوبر 2026، 14:10" (Dubai time). */
export function formatDateTime(value: string): string {
  return `${formatDate(value)}${current.dateTimeSeparator}${formatTime(value)}`;
}

/** Instant -> "14:10" (Dubai time). */
export function formatTime(value: string): string {
  return dateFormatter('time').format(parseInstant(value));
}

/** Instant -> "Wed 07 Oct". */
export function formatWeekdayDate(value: string): string {
  return dateFormatter('weekday').format(parseInstant(value));
}

/** "2026-10" -> "October 2026" */
export function formatPeriod(period: string): string {
  return dateFormatter('month').format(parseDateOnly(`${period}-01`));
}

/** "2026-10" -> "Oct 26" (chart axes and compact period labels). */
export function formatPeriodShort(period: string): string {
  const date = parseDateOnly(`${period}-01`);
  // English keeps fixed three-letter months ("Sep 26", never "Sept 26"), as in dates.
  if (current.intl === 'en-GB') return `${MONTHS[date.getUTCMonth()] ?? ''} ${String(date.getUTCFullYear() % 100).padStart(2, '0')}`;
  return dateFormatter('monthShort').format(date);
}

/** Instant -> "2 hours ago" / "yesterday" in the UI language. */
export function formatRelative(value: string, now: Date = new Date()): string {
  const rtf = memo<Intl.RelativeTimeFormat>(`rel|${currentLocale}`, () => new Intl.RelativeTimeFormat(currentLocale, { numeric: 'auto', style: 'short', numberingSystem: 'latn' } as Intl.RelativeTimeFormatOptions));
  const diffMs = parseInstant(value).getTime() - now.getTime();
  const minutes = Math.round(diffMs / 60_000);
  if (Math.abs(minutes) < 1) return rtf.format(0, 'minute');
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour');
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 30) return rtf.format(days, 'day');
  return formatDate(value);
}

/** One working day = 10 hours (08:00–18:00 GST). */
export const WORKING_DAY_MINUTES = 600;

/**
 * Signed working-time duration from the server's remaining minutes.
 * 200 -> "3h 20m", -160 -> "−2h 40m", 840 -> "1d 4h" (d = working day).
 */
export function formatWorkingDuration(minutes: number): string {
  const neg = minutes < 0;
  const abs = Math.abs(Math.trunc(minutes));
  // Intl narrow units: "3h 20m" in English, "3 س 20 د" in Arabic; digits stay Western.
  const unit = (n: number, u: 'day' | 'hour' | 'minute') =>
    memo(`unit|${u}|${currentLocale}`, () => new Intl.NumberFormat(currentLocale, { style: 'unit', unit: u, unitDisplay: 'narrow', numberingSystem: 'latn' })).format(n);
  let text: string;
  if (abs < 60) text = unit(abs, 'minute');
  else if (abs < WORKING_DAY_MINUTES) {
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    text = m === 0 ? unit(h, 'hour') : `${unit(h, 'hour')} ${unit(m, 'minute')}`;
  } else {
    const d = Math.floor(abs / WORKING_DAY_MINUTES);
    const h = Math.floor((abs % WORKING_DAY_MINUTES) / 60);
    text = h === 0 ? unit(d, 'day') : `${unit(d, 'day')} ${unit(h, 'hour')}`;
  }
  return neg ? `−${text}` : text;
}
